import api from "../../../services/api";
import axios from "axios";

const ADMIN_BASE_URL = "/admin/magazines";

export const adminMagazineAPI = {
  /**
   * Get paginated magazines for admin.
   */
  getAll: async (params = {}) => {
    const response = await api.get(`${ADMIN_BASE_URL}/`, { params });
    return response.data;
  },

  /**
   * Get single magazine with admin details.
   */
  getById: async (id) => {
    const response = await api.get(`${ADMIN_BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Get active system roles for Member-Only configuration.
   */
  getRoles: async () => {
    const response = await api.get(`${ADMIN_BASE_URL}/roles`);
    return response.data;
  },

  /**
   * Create a new magazine edition.
   */
  create: async (payload) => {
    const response = await api.post(`${ADMIN_BASE_URL}/`, payload);
    return response.data;
  },

  /**
   * Update magazine edition.
   */
  update: async (id, payload) => {
    const response = await api.put(`${ADMIN_BASE_URL}/${id}`, payload);
    return response.data;
  },

  /**
   * Delete magazine edition and all associated assets.
   */
  delete: async (id) => {
    const response = await api.delete(`${ADMIN_BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Request a presigned S3 PUT URL for direct client-side page upload.
   */
  getPresignedUploadUrl: async ({ object_key, content_type, filename, magazine_id }) => {
    const response = await api.post(`${ADMIN_BASE_URL}/presigned-upload`, {
      object_key,
      content_type,
      filename,
      magazine_id,
    });
    return response.data;
  },

  /**
   * Request a presigned S3 PUT URL for cover images.
   */
  getPresignedCoverUploadUrl: async ({ object_key, content_type, filename, magazine_id }) => {
    const response = await api.post(`${ADMIN_BASE_URL}/presigned-upload/cover`, {
      object_key,
      content_type,
      filename,
      magazine_id,
    });
    return response.data;
  },

  /**
   * Upload binary/blob directly to S3 via presigned PUT URL.
   * Completely bypasses backend server for maximum speed and zero memory overhead.
   */
  uploadToS3: async (presignedUrl, fileBlob, contentType, onProgress) => {
    return axios.put(presignedUrl, fileBlob, {
      headers: { "Content-Type": contentType },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
  },

  /**
   * Bulk register uploaded page keys into the database.
   */
  registerPages: async (magazineId, pages) => {
    const response = await api.post(`${ADMIN_BASE_URL}/${magazineId}/pages`, {
      pages,
    });
    return response.data;
  },

  /**
   * Reorder page numbers for an edition.
   */
  reorderPages: async (magazineId, pageOrders) => {
    const response = await api.put(`${ADMIN_BASE_URL}/${magazineId}/pages/reorder`, {
      pages: pageOrders,
    });
    return response.data;
  },

  /**
   * Delete a single page from a magazine.
   */
  deletePage: async (magazineId, pageId) => {
    const response = await api.delete(`${ADMIN_BASE_URL}/${magazineId}/pages/${pageId}`);
    return response.data;
  },

  /**
   * Get captured reader leads for a magazine.
   */
  getLeads: async (magazineId, params = {}) => {
    const response = await api.get(`${ADMIN_BASE_URL}/${magazineId}/leads`, { params });
    return response.data;
  },

  /**
   * Export captured leads as CSV.
   */
  exportLeadsCsv: async (magazineId) => {
    const response = await api.get(`${ADMIN_BASE_URL}/${magazineId}/leads/export`, {
      responseType: "blob",
    });
    return response.data;
  },

  /**
   * Get reader access requests for approval workflow.
   */
  getAccessRequests: async (magazineId, params = {}) => {
    const response = await api.get(`${ADMIN_BASE_URL}/${magazineId}/requests`, { params });
    return response.data;
  },

  /**
   * Resolve an access request (Approve or Reject).
   */
  resolveAccessRequest: async (magazineId, requestId, { action, reason, expires_days }) => {
    const response = await api.post(
      `${ADMIN_BASE_URL}/${magazineId}/requests/${requestId}/resolve`,
      {
        action,
        reason,
        expires_days,
      }
    );
    return response.data;
  },

  /**
   * Dynamically import and convert a PDF into a flipbook edition.
   * Sends multipart/form-data to POST /admin/magazines/import-pdf.
   */
  importPdf: async (formData, onProgress) => {
    const response = await api.post(`${ADMIN_BASE_URL}/import-pdf`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },
};

export default adminMagazineAPI;
